import { CommonModule } from '@angular/common'
import { Component, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Themes } from 'src/app/shared/enums/enums'

export interface NodeConfigDialogData {
  title: string
  infoTooltip?: string
  contentType: string
  config: Record<string, any>
}

export interface NodeConfigDialogResult {
  config: Record<string, any>
}

@Component({
  selector: 'node-config-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, AngularMaterialsModules],
  templateUrl: './node-config-dialog.component.html',
  styleUrls: ['./node-config-dialog.component.scss'],
})
export class NodeConfigDialogComponent implements OnDestroy {
  private themeService = inject(ThemeService)
  private themeSubscription: Subscription

  @Input() data!: NodeConfigDialogData
  @Output() configured = new EventEmitter<NodeConfigDialogResult>()
  @Output() cancelled = new EventEmitter<void>()

  _isLightTheme = true
  _isDarkTheme = false
  _isPaperTheme = false

  constructor() {
    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe()
  }

  // --- Attachment config ---
  triggerImagePicker(imageInput: HTMLInputElement): void {
    imageInput.click()
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.files?.length) return

    const maxImages = 3
    const existing: { name: string; dataUrl: string }[] = this.data.config['images'] ?? []
    const slotsLeft = maxImages - existing.length
    if (slotsLeft <= 0) return

    const filesToRead = Math.min(input.files.length, slotsLeft)
    let remaining = filesToRead

    for (let i = 0; i < filesToRead; i++) {
      const file = input.files[i]
      const reader = new FileReader()
      reader.onload = (): void => {
        existing.push({ name: file.name, dataUrl: reader.result as string })
        remaining--
        if (remaining === 0) {
          this.data.config['images'] = [...existing]
        }
      }
      reader.readAsDataURL(file)
    }

    input.value = ''
  }

  removeImage(index: number): void {
    const images: any[] = this.data.config['images'] ?? []
    images.splice(index, 1)
    this.data.config['images'] = [...images]
  }

  // --- Create figure config ---
  get figureStyle(): string {
    return this.data.config['figureStyle'] ?? 'bold'
  }

  set figureStyle(value: string) {
    this.data.config['figureStyle'] = value
  }

  // --- Create node config (prompt) ---
  get prompt(): string {
    return this.data.config['prompt'] ?? ''
  }

  set prompt(value: string) {
    this.data.config['prompt'] = value
  }

  get isFigureSubType(): boolean {
    return this.data.config['subType'] === 'figure'
  }

  configure(): void {
    this.configured.emit({ config: this.data.config })
  }

  cancel(): void {
    this.cancelled.emit()
  }
}
